import { useEffect, useState, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import html2pdf from 'html2pdf.js';
import {
  Box,
  Button,
  Typography,
  Stack,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  CardHeader,
  Avatar,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { InvoicePrintComponent } from '../components/InvoicePrintComponent';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { format } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import ArticleIcon from '@mui/icons-material/Article';

interface SaleRow {
  id: string;
  customer_name: string;
  total_amount: number;
  created_at: string;
  users: { email: string } | null;
  discount: number;
  notes: string;
  items?: SaleItem[];
}

interface SaleItem {
  id: string;
  item_id: string;
  quantity: number;
  price_at_sale: number;
  discount: number;
  items: {
    name: string;
    description: string;
  };
}

interface CompanyInfo {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  website: string;
  logo?: string;
}

const companyInfo: CompanyInfo = {
  name: "Your Business Name",
  address: "123 Business Street",
  city: "Business City",
  state: "BS",
  zip: "12345",
  phone: "(123) 456-7890",
  email: "contact@yourbusiness.com",
  website: "www.yourbusiness.com",
  logo: "https://via.placeholder.com/150x50?text=Your+Logo",
};

export function Invoices() {
  const [sales, setSales] = useState<SaleRow[]>([]);
  const [selectedSales, setSelectedSales] = useState<SaleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSale, setPreviewSale] = useState<SaleRow | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const printComponentRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  useEffect(() => {
    fetchSales();
  }, []);

  const fetchSales = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('sales')
        .select(`
          id,
          customer_name,
          total_amount,
          created_at,
          discount,
          notes,
          users ( email ),
          sale_items (
            id,
            item_id,
            quantity,
            price_at_sale,
            discount,
            items ( name, description )
          )
        `);
        
      if (error) throw error;
      
      // Map to expected format
      const formattedSales = (data || []).map(sale => ({
        ...sale,
        users: Array.isArray(sale.users) ? sale.users[0] : sale.users,
        items: sale.sale_items
      }));
      
      setSales(formattedSales as any);
    } catch (error) {
      console.error('Error fetching sales:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique clients for dropdown
  const uniqueClients = useMemo(() => {
    return Array.from(new Set(sales.map(s => s.customer_name))).sort();
  }, [sales]);

  const filteredSales = useMemo(() => {
    return sales.filter(sale => {
      const matchesSearch = !searchTerm || sale.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            sale.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (sale.users?.email && sale.users.email.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesClient = clientFilter ? sale.customer_name === clientFilter : true;
      const matchesMinAmt = minAmount ? Number(sale.total_amount) >= Number(minAmount) : true;
      const matchesMaxAmt = maxAmount ? Number(sale.total_amount) <= Number(maxAmount) : true;
      
      let matchesStart = true;
      let matchesEnd = true;
      if (startDate) {
        matchesStart = new Date(sale.created_at) >= new Date(startDate);
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setDate(end.getDate() + 1);
        matchesEnd = new Date(sale.created_at) < end;
      }
      return matchesSearch && matchesClient && matchesMinAmt && matchesMaxAmt && matchesStart && matchesEnd;
    });
  }, [sales, searchTerm, clientFilter, minAmount, maxAmount, startDate, endDate]);

  const handleSelectionChange = (selectionModel: GridRowSelectionModel) => {
    const selectedRows = sales.filter((sale) =>
      selectionModel.includes(sale.id)
    );
    setSelectedSales(selectedRows);
  };

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      if (printComponentRef.current) {
        const printWindow = window.open('', '_blank');
        
        if (printWindow) {
          printWindow.document.write(`
            <html>
              <head>
                <title>Invoice - ${selectedSales.length > 1 ? `${selectedSales.length} Sales` : selectedSales[0]?.customer_name}</title>
                <style>
                  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                  
                  body { 
                    font-family: 'Inter', Arial, sans-serif; 
                    margin: 0; 
                    padding: 30px; 
                    color: #000;
                    background: #fff;
                  }
                  
                  @media print {
                    body { margin: 0; padding: 20px; }
                    @page { size: auto; margin: 20mm; }
                    
                    .page-break {
                      page-break-after: always;
                      margin-bottom: 0;
                    }
                  }
                </style>
              </head>
              <body>
                ${printComponentRef.current.innerHTML}
              </body>
            </html>
          `);
          
          printWindow.document.close();
          printWindow.focus();
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
            setIsPrinting(false);
          }, 300);
        } else {
          console.error("Couldn't open print window");
          setIsPrinting(false);
        }
      } else {
        console.error("Print component reference is null");
        setIsPrinting(false);
      }
    }, 500);
  };
  const handleDownloadPDF = () => {
    if (printComponentRef.current) {
      const element = printComponentRef.current;
      const opt = {
        margin:       10,
        filename:     `Invoice_${selectedSales.length > 1 ? 'Batch' : selectedSales[0]?.customer_name}.pdf`,
        image:        { type: 'jpeg' as const, quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true },
        jsPDF:        { unit: 'mm' as const, format: 'a4' as const, orientation: 'portrait' as const }
      };
      
      const parent = element.parentElement;
      if (parent) parent.style.display = 'block';
      
      html2pdf().set(opt).from(element).save().then(() => {
        if (parent) parent.style.display = 'none';
      });
    }
  };

  const handlePreview = (sale: SaleRow) => {
    setPreviewSale(sale);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  const columns: GridColDef[] = [
    { 
      field: 'customer_name', 
      headerName: 'Customer', 
      width: 200,
      renderCell: (params: GridRenderCellParams<SaleRow>) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            sx={{ 
              width: 32, 
              height: 32, 
              bgcolor: theme.palette.primary.main,
              mr: 1,
              fontSize: '0.9rem'
            }}
          >
            {params.row.customer_name.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2" fontWeight={500} noWrap>
            {params.row.customer_name}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'total_amount',
      headerName: 'Amount',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<SaleRow>) => (
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'success.main',
            fontWeight: 600 
          }}
        >
          ₹{params.row.total_amount.toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Date',
      width: 170,
      renderCell: (params: GridRenderCellParams<SaleRow>) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', opacity: 0.7 }} />
          <Typography variant="body2">
            {format(new Date(params.row.created_at), 'MMM dd, yyyy')}
          </Typography>
        </Box>
      ),
      valueGetter: (params: GridValueGetterParams<SaleRow>) => 
        new Date(params.row.created_at).toISOString(),
    },
    {
      field: 'users',
      headerName: 'Employee',
      width: 180,
      renderCell: (params: GridRenderCellParams<SaleRow>) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', opacity: 0.7 }} />
          <Typography variant="body2">
            {params.row.users?.email?.split('@')[0] || 'Unknown'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'items',
      headerName: 'Items',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<SaleRow>) => (
        <Chip 
          label={params.row.items?.length || 0} 
          size="small"
          color="primary"
          variant={params.row.items?.length ? "filled" : "outlined"}
        />
      ),
      valueGetter: (params: GridValueGetterParams<SaleRow>) =>
        params.row.items?.length || 0,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams<SaleRow>) => (
        <Box>
          <Tooltip title="Preview Invoice">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                handlePreview(params.row);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print Invoice">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                setSelectedSales([params.row]);
                setTimeout(handlePrint, 100);
              }}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box className="fade-in" sx={{ pt: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card 
            elevation={0} 
            sx={{ 
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}
          >
            <CardHeader
              title={
                <Typography variant="h6" fontWeight={600}>
                  Sales Invoices
                </Typography>
              }
              action={
                <TextField
                  size="small"
                  placeholder="Search..." 
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ width: 200 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              }
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 2, pb: 1, height: 'calc(100% - 72px)' }}>
              <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  select
                  label="Client"
                  size="small"
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  sx={{ minWidth: 150 }}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="">All Clients</option>
                  {uniqueClients.map(c => <option key={c} value={c}>{c}</option>)}
                </TextField>
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Min Amount"
                  type="number"
                  size="small"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  sx={{ width: 120 }}
                />
                <TextField
                  label="Max Amount"
                  type="number"
                  size="small"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  sx={{ width: 120 }}
                />
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DataGrid
                  rows={filteredSales}
                  columns={columns}
                  checkboxSelection
                  onRowSelectionModelChange={handleSelectionChange}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                    sorting: {
                      sortModel: [{ field: 'created_at', sort: 'desc' }],
                    },
                  }}
                  pageSizeOptions={[10, 25, 50]}
                  getRowId={(row) => row.id}
                  sx={{
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                    },
                    '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                      outline: 'none !important',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)',
                    },
                    border: 'none',
                    height: 600
                  }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardHeader
              title={
                <Typography variant="h6" fontWeight={600}>
                  Generate Invoice
                </Typography>
              }
              avatar={
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                  <ArticleIcon />
                </Avatar>
              }
            />
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                Select one or more sales from the list to generate invoices. You can print 
                them individually or in batch.
              </Typography>
              {selectedSales.length > 0 ? (
                <Box>
                  <Box
                    sx={{ 
                      p: 2, 
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                      borderRadius: 2,
                      mb: 3,
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Sales: {selectedSales.length}
                    </Typography>
                    <Stack spacing={1} sx={{ maxHeight: 200, overflowY: 'auto' }}>
                      {selectedSales.map(sale => (
                        <Box 
                          key={sale.id} 
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Typography variant="body2" noWrap sx={{ maxWidth: '75%' }}>
                            {sale.customer_name}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            ₹{sale.total_amount.toFixed(2)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        Total: <strong>₹{selectedSales.reduce((sum, sale) => sum + sale.total_amount, 0).toFixed(2)}</strong> 
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={isPrinting ? <CircularProgress size={24} color="inherit" /> : <PrintIcon />}
                    onClick={handlePrint}
                    disabled={isPrinting}
                    sx={{ mb: 2 }}
                  >
                    {isPrinting ? 'Preparing...' : 'Print Invoices'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadPDF}
                    sx={{ mb: 2 }}
                  >
                    Download as PDF
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: 3,
                  }}
                >
                  <ArticleIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                    No Sales Selected
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select one or more sales from the table to generate invoices
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box style={{ display: 'none' }}>
        <Box ref={printComponentRef}>
          <InvoicePrintComponent sales={selectedSales} companyInfo={companyInfo} />
        </Box>
      </Box>

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Invoice Preview</Typography>
          <IconButton edge="end" color="inherit" onClick={handleClosePreview} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {previewSale && (
            <InvoicePrintComponent sales={[previewSale]} companyInfo={companyInfo} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
          <Button 
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => {
              if (previewSale) {
                setSelectedSales([previewSale]);
                setTimeout(() => {
                  handlePrint();
                  handleClosePreview();
                }, 100);
              }
            }}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}